import React, { useState } from 'react'
import { MdMailOutline, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import LoginHelpTrigger from '@/components/auth/login/LoginHelpTrigger'
import PasswordStrength from '@/components/auth/login/PasswordStrength'
import type { Identifier } from '@/hooks/useLoginFlow'
import type { ValidationErrors } from '@/types'

/**
 * Stage 2 of the add-email flow. Backend has emailed a 6-digit code
 * to `pendingEmail`; the customer types it here alongside the
 * password they want to set on the new account. On submit the
 * backend verifies the code, PATCHes Dynamics, creates the user, and
 * returns a session token — the customer lands authenticated.
 *
 * Combined into one screen on purpose: two POSTs (verify, then set
 * password) would mean two failure points and an awkward
 * almost-claimed state if the second one bombs. One submit, one
 * outcome.
 */

interface Props {
  identifier: Identifier
  pendingEmail: string
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onSubmit: (code: string, password: string, passwordConfirmation: string) => void
  onResend: () => void
  onBack: () => void
}

function LoginAddEmailVerifyStep({
  identifier,
  pendingEmail,
  loading,
  apiError,
  fieldErrors,
  onSubmit,
  onResend,
  onBack,
}: Props): React.JSX.Element {
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [strongEnough, setStrongEnough] = useState(false)

  const passwordsMatch = confirm.length > 0 && password === confirm
  const codeIsSixDigits = /^\d{6}$/.test(code)
  const canSubmit =
    !loading && codeIsSixDigits && strongEnough && passwordsMatch

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const next: ValidationErrors = {}
    if (!codeIsSixDigits) next.code = 'Enter the 6-digit code from your email'
    if (!strongEnough) next.password = 'Password does not meet the requirements'
    if (!passwordsMatch) next.passwordConfirmation = 'Passwords do not match'
    setErrors(next)
    if (Object.keys(next).length === 0) onSubmit(code, password, confirm)
  }

  // Strip non-digits and clamp at 6. Lets the customer paste "123 456"
  // or "123-456" without the form being fussy.
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
  }

  return (
    <div className="login-step">
      <div className="login-step-icon login-step-icon--success">
        <MdMailOutline size={48} />
      </div>
      <h1 className="login-step-heading">Check your email</h1>
      <p className="login-step-subheading">
        We've sent a 6-digit code to <strong>{pendingEmail}</strong>. Enter
        it below along with the password you want to use, and we'll get
        you signed in.
      </p>

      <ValidationSummary errors={fieldErrors} message={apiError} />

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="addEmailCode" className="form-label">
            Verification code
          </label>
          <input
            id="addEmailCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            className={`form-control form-control-lg login-otp-input ${errors.code ? 'is-invalid' : ''}`}
            value={code}
            onChange={handleCodeChange}
            disabled={loading}
            maxLength={6}
            placeholder="000000"
          />
          {errors.code && (
            <span className="form-text is-invalid">{errors.code}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="addEmailPassword" className="form-label">
            Choose a password
          </label>
          <div className="password-wrapper">
            <input
              id="addEmailPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-control form-control-lg ${errors.password ? 'is-invalid' : ''}`}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((p) => !p)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </button>
          </div>
          {errors.password && (
            <span className="form-text is-invalid">{errors.password}</span>
          )}
          <PasswordStrength
            password={password}
            onValidityChange={setStrongEnough}
          />
        </div>

        <div className="form-group">
          <label htmlFor="addEmailPasswordConfirmation" className="form-label">
            Confirm password
          </label>
          <input
            id="addEmailPasswordConfirmation"
            type={showPassword ? 'text' : 'password'}
            className={`form-control form-control-lg ${errors.passwordConfirmation ? 'is-invalid' : ''}`}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={loading}
          />
          {errors.passwordConfirmation && (
            <span className="form-text is-invalid">{errors.passwordConfirmation}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={!canSubmit}
        >
          {loading ? 'Verifying…' : 'Verify and sign in'}
        </button>

        <div className="login-step-secondary">
          <button
            type="button"
            className="btn btn-link"
            onClick={onResend}
            disabled={loading}
          >
            Didn't get a code? Resend
          </button>
        </div>
      </form>

      <div className="login-step-footer">
        <button type="button" className="btn btn-link" onClick={onBack}>
          Back
        </button>
        <LoginHelpTrigger identifier={identifier} />
      </div>
    </div>
  )
}

export default LoginAddEmailVerifyStep
